import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Languages, BadgeCheck, MessageCircleQuestion } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { WhereWeDrive } from "@/components/WhereWeDrive";

function DriverCard({ d }) {
  const langs = (d.languages || []).join(" & ") || "English & Spanish";
  return (
    <div data-testid={`driver-card-${d.id}`}
      className="overflow-hidden rounded-3xl bg-white border border-sand-200 shadow-[0_8px_32px_rgba(44,42,41,0.06)] transition-transform hover:-translate-y-1">
      {d.photo && <img src={d.photo} alt={d.name} className="aspect-square w-full object-cover" />}
      <div className="p-6">
        <h3 className="text-xl font-medium text-charcoal-800">{d.name}</h3>
        {d.years_experience != null && (
          <p className="text-xs text-charcoal-400 mt-0.5">{d.years_experience}+ years on the road</p>
        )}
        <p className="mt-3 text-sm leading-relaxed text-charcoal-600">{d.bio}</p>
        <div className="mt-4 space-y-2">
          {d.background_checked && (
            <p data-testid={`driver-badge-check-${d.id}`} className="inline-flex w-full items-center gap-2 rounded-full bg-sage-light px-3 py-1.5 text-xs font-medium text-sage-700">
              <ShieldCheck size={13} /> Background-checked — no criminal record
            </p>
          )}
          <p data-testid={`driver-badge-lang-${d.id}`} className="inline-flex w-full items-center gap-2 rounded-full bg-sand-100 px-3 py-1.5 text-xs font-medium text-charcoal-700">
            <Languages size={13} /> Bilingual — {langs}
          </p>
          {d.license_type && (
            <p data-testid={`driver-badge-license-${d.id}`} className="inline-flex w-full items-center gap-2 rounded-full bg-terracotta-light/50 px-3 py-1.5 text-xs font-medium text-clay-800">
              <BadgeCheck size={13} /> Licensed: {d.license_type}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MeetOurDrivers() {
  const [drivers, setDrivers] = useState(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/drivers").then((r) => setDrivers(r.data)).catch(() => setDrivers([]));
  }, []);

  return (
    <div data-testid="page-drivers">
      <Seo title="Meet Our Drivers" path="/drivers"
        description="The background-checked, bilingual, licensed drivers who move you, your vehicle and your pets from the US to Mérida — meet them before you book." />
      <section className="bg-sand-100">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28">
          <div className="max-w-2xl animate-fade-up">
            <p className="text-sm uppercase tracking-[0.2em] text-clay-700 font-medium mb-4">Meet Our Drivers</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-charcoal-900 leading-[1.05]">
              Know who's behind the wheel — before you book
            </h1>
            <p className="mt-6 text-lg text-charcoal-600 leading-relaxed">
              Every Hola Movers driver is background-checked, licensed and bilingual. These are the people
              who will drive you, your vehicle or your pets door-to-door — so say hola before the trip.
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-6 lg:px-12 py-16 space-y-16">
        {!drivers ? (
          <p className="text-charcoal-500">Loading…</p>
        ) : drivers.length === 0 ? (
          <p className="text-charcoal-500">Driver profiles are coming soon — ask us anything in the meantime.</p>
        ) : (
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {drivers.map((d) => <DriverCard key={d.id} d={d} />)}
          </div>
        )}

        <WhereWeDrive />

        <div data-testid="driver-questions-cta" className="rounded-3xl bg-sage-light p-10 lg:p-14 text-center">
          <MessageCircleQuestion size={28} className="mx-auto text-sage-700" />
          <h2 className="mt-4 text-3xl font-normal text-charcoal-800">Have questions about our drivers before you book?</h2>
          <p className="mt-3 text-charcoal-600 max-w-xl mx-auto">
            Ask us anything — vetting, routes, how pets ride, who you'll get. We'll answer honestly before you commit to anything.
          </p>
          <Link to="/contact?about=drivers" data-testid="driver-questions-link"
            className="mt-8 inline-block rounded-full bg-terracotta px-8 py-3 text-white font-medium hover:bg-terracotta-hover transition-colors">
            Ask about our drivers
          </Link>
        </div>
      </div>
    </div>
  );
}
